import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMarketData } from '../contexts/MarketDataContext';
import {
  Wallet,
  Plus,
  Minus,
  ArrowUpRight,
  ArrowDownLeft,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  CreditCard,
  Banknote,
  Bitcoin,
  Eye,
  EyeOff,
  Shield,
  Info,
  Layers,
  Calendar,
  Percent,
  Calculator,
  ArrowRight,
  Lock,
  X,
  ChevronDown
} from 'lucide-react';
import { useDatabase } from '../hooks/useDatabase';

interface StakingPageProps {
  usdtBalance: number;
  btcBalance: number;
  currentBtcPrice: number;
  onStake: (asset: string, amount: number, duration: number) => Promise<boolean>;
  availableBalance?: number;
}

interface StakingAsset {
  id: string;
  name: string;
  symbol: string;
  iconUrl: string;
  apy: number;
  minStake: number;
  lockPeriods: number[]; // in days
  totalStaked: number;
  balance?: number;
  price?: number;
}

const StakingPage: React.FC<StakingPageProps> = ({
  usdtBalance,
  btcBalance,
  currentBtcPrice,
  onStake,
  availableBalance
}) => {
  const { t } = useTranslation();
  const { isConnected: isRealtimeConnected } = useMarketData();
  const { userStakes, addUserStake, calculateCurrentEarnings, cancelUserStake, claimUserStake, fetchUserStakes, coingeckoMarketCapData } = useDatabase();
  
  // State for staking form
  const [stakingAmount, setStakingAmount] = useState('');
  const [stakingDuration, setStakingDuration] = useState(30);
  const [selectedAsset, setSelectedAsset] = useState<StakingAsset | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  
  // State for staking operations
  const [isStaking, setIsStaking] = useState(false);
  const [stakingMessage, setStakingMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  
  // State for expanded asset details
  const [expandedAssetId, setExpandedAssetId] = useState<string | null>(null);
  
  // State for calculator
  const [calculatorAsset, setCalculatorAsset] = useState('btc-stake');
  const [calculatorAmount, setCalculatorAmount] = useState('1');
  const [calculatorDuration, setCalculatorDuration] = useState('30');
  const [claimingStakes, setClaimingStakes] = useState<Record<string, boolean>>({});
  const [calculatorResults, setCalculatorResults] = useState({
    estimatedEarnings: 0,
    estimatedEarningsUsd: 0,
    daily: 0,
    weekly: 0,
    monthly: 0,
    yearly: 0
  });

  // State for custom dropdowns
  const [isAssetDropdownOpen, setIsAssetDropdownOpen] = useState(false);
  const [isDurationDropdownOpen, setIsDurationDropdownOpen] = useState(false);

  // Helper function to render crypto icon with fallback
  const renderCryptoIcon = (currency: { symbol: string, iconUrl: string }) => {
    return (
      <div className="relative w-10 h-10 overflow-hidden flex items-center justify-center">
        <img
          src={currency.iconUrl}
          alt={currency.symbol}
          className="w-full h-full object-contain"
          onError={(e) => {
            // On error, replace with text icon
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
        <div className="absolute inset-0 hidden flex items-center justify-center text-white font-bold">
          {currency.symbol.substring(0, 2)}
        </div>
      </div>
    );
  };

  // Available staking assets
  const [availableStakingAssets, setAvailableStakingAssets] = useState<StakingAsset[]>([
    {
      id: 'btc-stake',
      name: 'Bitcoin',
      symbol: 'BTC',
      iconUrl: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
      apy: 5.5,
      minStake: 0.001,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 1250.75,
      balance: btcBalance,
      price: currentBtcPrice
    },
    {
      id: 'usdt-stake',
      name: 'Tether',
      symbol: 'USDT',
      iconUrl: 'https://assets.coingecko.com/coins/images/325/large/Tether.png',
      apy: 12.0,
      minStake: 100,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 5750000,
      balance: availableBalance || 0,
      price: 1
    },
    {
      id: 'eth-stake',
      name: 'Ethereum',
      symbol: 'ETH',
      iconUrl: 'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
      apy: 7.2,
      minStake: 0.01,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 32500.5,
      balance: 0,
      price: 3500
    },
    {
      id: 'sol-stake',
      name: 'Solana',
      symbol: 'SOL',
      iconUrl: 'https://assets.coingecko.com/coins/images/4128/large/solana.png',
      apy: 9.8,
      minStake: 1,
      lockPeriods: [30, 60, 90, 180, 365],
      totalStaked: 185000,
      balance: 0,
      price: 150
    }
  ]);

  // Update icon URLs from CoinGecko data when available
  useEffect(() => {
    if (coingeckoMarketCapData && coingeckoMarketCapData.length > 0) {
      setAvailableStakingAssets(prev => 
        prev.map(asset => {
          const coinData = coingeckoMarketCapData.find(
            coin => coin.symbol.toLowerCase() === asset.symbol.toLowerCase()
          );
          
          if (coinData && coinData.image) {
            return { ...asset, iconUrl: coinData.image };
          }
          return asset;
        })
      );
    }
  }, [coingeckoMarketCapData]);

  // Fetch user stakes on mount
  useEffect(() => {
    fetchUserStakes();
  }, [fetchUserStakes]);

  // Update asset balances when they change
  useEffect(() => {
    setAvailableStakingAssets(prev => 
      prev.map(asset => {
        if (asset.symbol === 'BTC') {
          return { ...asset, balance: btcBalance, price: currentBtcPrice };
        } else if (asset.symbol === 'USDT') {
          return { ...asset, balance: availableBalance || usdtBalance, price: 1 };
        }
        return asset;
      })
    );
  }, [btcBalance, usdtBalance, currentBtcPrice, availableBalance]);

  // Clear staking message after 5 seconds
  useEffect(() => {
    if (stakingMessage) {
      const timer = setTimeout(() => {
        setStakingMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [stakingMessage]);

  // Update calculator results when inputs change
  useEffect(() => {
    const updateCalculatorResults = () => {
      const asset = availableStakingAssets.find(a => a.id === calculatorAsset);
      if (!asset) return;

      const amount = parseFloat(calculatorAmount) || 0;
      const days = parseInt(calculatorDuration) || 30;
      const apy = asset.apy;

      // Calculate daily rate
      const dailyRate = apy / 365 / 100;
      
      // Calculate earnings
      const estimatedEarnings = amount * dailyRate * days;
      const estimatedEarningsUsd = estimatedEarnings * (asset.price || 0);
      
      // Calculate periodic earnings
      const daily = amount * dailyRate;
      const weekly = daily * 7;
      const monthly = daily * 30;
      const yearly = amount * (apy / 100);
      
      setCalculatorResults({
        estimatedEarnings,
        estimatedEarningsUsd,
        daily,
        weekly,
        monthly,
        yearly
      });
    };
    
    updateCalculatorResults();
  }, [calculatorAsset, calculatorAmount, calculatorDuration, availableStakingAssets]);

  // Calculate estimated return based on amount, duration, and APY
  const calculateEstimatedReturn = (amount: number, durationDays: number, apy: number) => {
    // Convert APY to daily rate
    const dailyRate = apy / 365 / 100;
    
    // Calculate interest for the staking period
    const interest = amount * dailyRate * durationDays;
    
    return interest;
  };

  // Handle stake button click
  const handleStakeClick = (asset: StakingAsset) => {
    setSelectedAsset(asset);
    setStakingAmount('');
    setStakingDuration(asset.lockPeriods[0]);
    setShowConfirmationModal(true);
  };

  // Check if user already has an active stake for the selected asset
  const hasActiveStakeForAsset = (assetSymbol: string) => {
    return userStakes.some(stake => 
      stake.asset_symbol === assetSymbol && 
      stake.status === 'active'
    );
  };

  // Confirm stake
  const confirmStake = async () => {
    if (!selectedAsset) return;
    
    const amount = parseFloat(stakingAmount);
    
    // Validate input
    if (isNaN(amount) || amount <= 0) {
      setStakingMessage({
        type: 'error',
        text: t('staking.enterValidAmount')
      });
      return;
    }
    
    // Check minimum stake
    if (amount < selectedAsset.minStake) {
      setStakingMessage({
        type: 'error',
        text: t('staking.minimumStakeAmount', { amount: selectedAsset.minStake, asset: selectedAsset.symbol })
      });
      return;
    }
    
    // Check available balance
    const stakingValueUSD = selectedAsset.symbol === 'USDT' ? amount : amount * currentBtcPrice;
    
    if (availableBalance !== undefined && stakingValueUSD > availableBalance) {
      setStakingMessage({
        type: 'error',
        text: t('staking.insufficientAvailableBalance', { 
          required: stakingValueUSD.toFixed(2), 
          available: availableBalance.toFixed(2) 
        })
      });
      return;
    }
    
    // Check balance
    const actualBalance = selectedAsset.symbol === 'USDT' && availableBalance !== undefined 
      ? Math.min(selectedAsset.balance || 0, availableBalance) 
      : selectedAsset.balance;
    
    if (actualBalance && amount > actualBalance) {
      setStakingMessage({
        type: 'error',
        text: t('staking.insufficientBalance', { asset: selectedAsset.symbol })
      });
      return;
    }
    
    // Check if user already has an active stake for this asset
    if (hasActiveStakeForAsset(selectedAsset.symbol)) {
      setStakingMessage({
        type: 'error',
        text: t('staking.alreadyHaveActiveStake', { asset: selectedAsset.symbol })
      });
      return;
    }
    
    setIsStaking(true);
    
    try {
      // Call the addUserStake function
      const success = await addUserStake(
        selectedAsset.symbol,
        amount,
        selectedAsset.apy,
        stakingDuration
      );
      
      if (success) {
        setStakingMessage({
          type: 'success',
          text: t('staking.stakingSuccessful', { 
            amount: amount, 
            asset: selectedAsset.symbol, 
            duration: stakingDuration 
          })
        });
        
        // Close modal and reset form
        setShowConfirmationModal(false);
        setStakingAmount('');
        setSelectedAsset(null);
        
        // Call the onStake prop to update balances
        await onStake(selectedAsset.symbol, amount, stakingDuration);
        
        // Refresh user stakes
        await fetchUserStakes();
      } else {
        throw new Error('Staking failed');
      }
    } catch (error: any) {
      setStakingMessage({
        type: 'error',
        text: error.message || t('staking.stakingFailed')
      });
    } finally {
      setIsStaking(false);
    }
  };

  // Handle max button click
  const handleMaxClick = () => {
    if (selectedAsset) {
      const actualBalance = selectedAsset.symbol === 'USDT' ? (availableBalance || usdtBalance) : selectedAsset.balance;
      
      if (actualBalance) {
        setStakingAmount(actualBalance.toString());
      }
    }
  };

  // Toggle asset details expansion
  const toggleAssetDetails = (assetId: string) => {
    if (expandedAssetId === assetId) {
      setExpandedAssetId(null);
    } else {
      setExpandedAssetId(assetId);
    }
  };

  // Calculate time remaining for a stake
  const calculateTimeRemaining = (endDate: string) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();
    
    if (diff <= 0) return t('staking.completed');
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return `${days}d ${hours}h ${t('staking.remaining')}`;
  };

  // Calculate progress percentage for a stake
  const calculateProgress = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    
    if (elapsed <= 0) return 0;
    if (elapsed >= total) return 100;
    
    return (elapsed / total) * 100;
  };

  // Get active stakes
  const activeStakes = userStakes.filter(stake => stake.status === 'active');
  
  // Get completed stakes that can be claimed
  const claimableStakes = userStakes.filter(stake => 
    stake.status === 'active' && new Date(stake.end_date) <= new Date()
  );
  
  // Get completed stakes
  const completedStakes = userStakes.filter(stake => stake.status === 'completed');

  return (
    <div className="min-h-screen bg-slate-50/95 p-8">
      {/* Header */}
      <div className="mb-8 pb-6" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ color: '#1e293b' }}>
            {t('staking.title')}
          </h1>
        </div>
        <p className="text-sm uppercase tracking-wider mt-2" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>
          {t('staking.subtitle')}
        </p>
      </div>

      {/* Status Messages */}
      {stakingMessage && (
        <div className={`mb-6 p-4 flex items-center gap-3 transition-all duration-300 ${
          stakingMessage.type === 'success'
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-red-50 text-red-700'
        }`} style={{
          border: `1px solid ${stakingMessage.type === 'success' ? '#10b981' : '#dc2626'}`,
          borderRadius: '12px',
          boxShadow: stakingMessage.type === 'success'
            ? '0 4px 12px rgba(16, 185, 129, 0.2)'
            : '0 4px 12px rgba(220, 38, 38, 0.2)'
        }}>
          {stakingMessage.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
          <span style={{ fontWeight: 600 }}>{stakingMessage.text}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Available Staking Assets */}
        <div className="lg:col-span-2 space-y-8">
          {/* Available Staking Assets */}
          <div className="relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #d91222, #b91020)'
            }} />

            <div className="flex items-center justify-between mb-6 pb-4" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center" style={{
                  background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(185, 16, 32, 0.15))',
                  border: '2px solid rgba(217, 18, 34, 0.3)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(217, 18, 34, 0.2)'
                }}>
                  <Layers size={20} style={{ color: '#d91222' }} />
                </div>
                <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.availableStakingAssets')}</h2>
              </div>
              <div className="px-3 py-1.5 text-xs uppercase tracking-wider font-bold" style={{
                background: 'rgba(0, 0, 0, 0.04)',
                color: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '8px'
              }}>
                {availableStakingAssets.length} {t('staking.assets')}
              </div>
            </div>

            <div className="space-y-4">
              {availableStakingAssets.map((asset) => {
                const hasActiveStake = hasActiveStakeForAsset(asset.symbol);

                return (
                  <div key={asset.id} className="relative overflow-hidden group transition-all duration-300" style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: '20px',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.06)';
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, rgba(217, 18, 34, 0.5), rgba(185, 16, 32, 0.5))'
                    }} />

                    {/* Asset Header */}
                    <div className="p-5 flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="relative w-12 h-12 overflow-hidden flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{
                          background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.08), rgba(217, 18, 34, 0.04))',
                          border: '2px solid rgba(217, 18, 34, 0.15)',
                          borderRadius: '14px',
                          boxShadow: '0 2px 8px rgba(217, 18, 34, 0.1)'
                        }}>
                          {renderCryptoIcon(asset)}
                        </div>
                        <div>
                          <div className="font-bold" style={{ color: '#1e293b', fontSize: '16px' }}>{asset.name}</div>
                          <div className="text-xs uppercase tracking-wider font-bold px-2 py-0.5 inline-block" style={{
                            color: 'rgba(0, 0, 0, 0.6)',
                            background: 'rgba(0, 0, 0, 0.04)',
                            borderRadius: '6px'
                          }}>{asset.symbol}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 md:gap-6 flex-1 justify-end">
                        <div className="text-right hidden sm:block">
                          <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>APY</div>
                          <div className="font-mono text-base font-bold px-2 py-1" style={{
                            color: '#10b981',
                            background: 'rgba(16, 185, 129, 0.1)',
                            borderRadius: '6px'
                          }}>{asset.apy}%</div>
                        </div>

                        <div className="text-right mr-2">
                          <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('common.balance')}</div>
                          <div className="font-mono text-sm md:text-base font-bold" style={{ color: '#1e293b' }}>
                            {(asset.symbol === 'USDT'
                              ? (availableBalance !== undefined ? availableBalance : usdtBalance)
                              : asset.balance || 0
                            ).toFixed(asset.symbol === 'USDT' ? 2 : 6)} {asset.symbol}
                          </div>
                        </div>

                        <button
                          onClick={() => toggleAssetDetails(asset.id)}
                          className="w-10 h-10 flex items-center justify-center transition-all duration-300 flex-shrink-0"
                          style={{
                            background: expandedAssetId === asset.id
                              ? 'linear-gradient(135deg, #d91222 0%, #b91020 100%)'
                              : 'linear-gradient(135deg, rgba(217, 18, 34, 0.1), rgba(185, 16, 32, 0.1))',
                            border: '2px solid rgba(217, 18, 34, 0.3)',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(217, 18, 34, 0.2)'
                          }}
                        >
                          {expandedAssetId === asset.id ? (
                            <Minus size={18} className="text-white" />
                          ) : (
                            <Plus size={18} style={{ color: '#d91222' }} />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedAssetId === asset.id && (
                      <div className="p-5" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
                          <div className="p-3" style={{
                            background: 'rgba(59, 130, 246, 0.05)',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            borderRadius: '12px'
                          }}>
                            <div className="text-xs font-bold mb-1.5 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('common.min')} Stake</div>
                            <div className="font-mono font-bold" style={{ color: '#1e293b' }}>{asset.minStake} {asset.symbol}</div>
                          </div>
                          <div className="p-3" style={{
                            background: 'rgba(16, 185, 129, 0.05)',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            borderRadius: '12px'
                          }}>
                            <div className="text-xs font-bold mb-1.5 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>Total Staked</div>
                            <div className="font-mono font-bold" style={{ color: '#1e293b' }}>{asset.totalStaked.toLocaleString()} {asset.symbol}</div>
                          </div>
                          <div className="p-3" style={{
                            background: 'rgba(217, 18, 34, 0.05)',
                            border: '1px solid rgba(217, 18, 34, 0.2)',
                            borderRadius: '12px'
                          }}>
                            <div className="text-xs font-bold mb-1.5 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('common.price')}</div>
                            <div className="font-mono font-bold" style={{ color: '#1e293b' }}>${(asset.price || 0).toLocaleString()}</div>
                          </div>
                        </div>

                        <div className="mb-5">
                          <div className="text-xs font-bold mb-3 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Available Lock Periods</div>
                          <div className="flex flex-wrap gap-2">
                            {asset.lockPeriods.map((period) => (
                              <div key={period} className="px-3 py-2 text-xs text-white font-bold transition-transform duration-300 hover:scale-105" translate="no" style={{
                                background: 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                                borderRadius: '10px',
                                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.3)'
                              }}>
                                {period} {t('staking.days')}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <button
                            onClick={() => handleStakeClick(asset)}
                            disabled={!asset.balance || asset.balance < asset.minStake || hasActiveStake}
                            className={`px-6 py-3 font-bold transition-all duration-300 uppercase tracking-wider text-xs flex items-center gap-2 ${
                              hasActiveStake ? 'cursor-not-allowed opacity-50' : ''
                            }`}
                            style={{
                              background: (!asset.balance || asset.balance < asset.minStake || hasActiveStake)
                                ? 'rgba(0, 0, 0, 0.05)'
                                : 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                              color: (!asset.balance || asset.balance < asset.minStake || hasActiveStake) ? 'rgba(0, 0, 0, 0.3)' : '#FFFFFF',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: (!asset.balance || asset.balance < asset.minStake || hasActiveStake)
                                ? 'none'
                                : '0 4px 12px rgba(217, 18, 34, 0.3)'
                            }}
                          >
                            <Layers size={16} />
                            {hasActiveStake ? t('staking.alreadyStaked') : `${t('staking.stake')} ${asset.symbol}`}
                          </button>
                        </div>

                        {hasActiveStake && (
                          <div className="mt-4 text-xs flex items-center gap-2 p-3" style={{
                            color: '#dc2626',
                            background: 'rgba(220, 38, 38, 0.1)',
                            border: '1px solid rgba(220, 38, 38, 0.3)',
                            borderRadius: '10px',
                            fontWeight: 600
                          }}>
                            <AlertTriangle size={14} />
                            <span>{t('staking.alreadyHaveActiveStake', { asset: asset.symbol })}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Active Stakes */}
          <div className="relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #d91222, #b91020)'
            }} />

            <div className="flex items-center gap-3 mb-6 pb-4" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <div className="w-10 h-10 flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(185, 16, 32, 0.15))',
                border: '2px solid rgba(217, 18, 34, 0.3)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.2)'
              }}>
                <TrendingUp size={20} style={{ color: '#d91222' }} />
              </div>
              <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.yourActiveStakes')}</h2>
            </div>

            {activeStakes.length > 0 ? (
              <div className="space-y-4">
                {activeStakes.map((stake) => {
                  const asset = availableStakingAssets.find(a => a.symbol === stake.asset_symbol);
                  const progress = calculateProgress(stake.start_date, stake.end_date);
                  const timeRemaining = calculateTimeRemaining(stake.end_date);
                  const currentEarnings = calculateCurrentEarnings(stake);
                  const isMatured = new Date(stake.end_date) <= new Date();

                  return (
                    <div key={stake.id} className="relative overflow-hidden group transition-all duration-300" style={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      borderRadius: '20px',
                      padding: '20px',
                      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.06)';
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #d91222, #b91020)'
                      }} />

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {asset ? (
                            <div className="relative w-12 h-12 overflow-hidden flex items-center justify-center" style={{
                              background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.08), rgba(217, 18, 34, 0.04))',
                              border: '2px solid rgba(217, 18, 34, 0.15)',
                              borderRadius: '14px',
                              boxShadow: '0 2px 8px rgba(217, 18, 34, 0.1)'
                            }}>
                              {renderCryptoIcon(asset)}
                            </div>
                          ) : (
                            <div className="w-12 h-12 flex items-center justify-center font-bold" style={{
                              background: 'rgba(0, 0, 0, 0.04)',
                              color: '#1e293b',
                              border: '2px solid rgba(0, 0, 0, 0.1)',
                              borderRadius: '14px'
                            }}>
                              {stake.asset_symbol.substring(0, 2)}
                            </div>
                          )}
                          <div>
                            <div className="font-bold" style={{ color: '#1e293b' }}>{asset?.name || stake.asset_symbol}</div>
                            <div className="text-xs font-mono font-bold" style={{ color: 'rgba(0, 0, 0, 0.5)' }} translate="no">{stake.staked_amount.toFixed(stake.asset_symbol === 'USDT' ? 2 : 6)} {stake.asset_symbol}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono font-bold px-2 py-1" style={{
                            color: '#10b981',
                            background: 'rgba(16, 185, 129, 0.1)',
                            borderRadius: '6px'
                          }}>{stake.apy_rate}% APY</div>
                          <div className="text-xs font-bold mt-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                            {new Date(stake.end_date).toLocaleDateString()} {t('staking.endDate')}
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full h-2 mb-3" style={{
                        background: 'rgba(0, 0, 0, 0.06)',
                        borderRadius: '10px',
                        overflow: 'hidden'
                      }}>
                        <div
                          className="h-2 transition-all duration-500"
                          style={{
                            width: `${progress}%`,
                            background: 'linear-gradient(90deg, #d91222, #b91020)',
                            borderRadius: '10px'
                          }}
                        ></div>
                      </div>

                      <div className="flex justify-between text-xs font-bold mb-4" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                        <div>{t('staking.started')}: {new Date(stake.start_date).toLocaleDateString()}</div>
                        <div>{t('staking.ends')}: {new Date(stake.end_date).toLocaleDateString()}</div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.earnedSoFar')}</div>
                          <div className="font-mono font-bold px-2 py-1 inline-block" style={{
                            color: '#10b981',
                            background: 'rgba(16, 185, 129, 0.1)',
                            borderRadius: '6px'
                          }} translate="no">{currentEarnings.toFixed(6)} {stake.asset_symbol}</div>
                        </div>
                        {isMatured ? (
                          <button
                            onClick={() => claimUserStake(stake.id)}
                            disabled={claimingStakes[stake.id]}
                            className="px-4 py-2 font-bold transition-all duration-300 uppercase tracking-wider text-xs flex items-center gap-2"
                            style={{
                              background: claimingStakes[stake.id]
                                ? 'rgba(0, 0, 0, 0.05)'
                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: claimingStakes[stake.id] ? 'rgba(0, 0, 0, 0.3)' : '#FFFFFF',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: claimingStakes[stake.id] ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.3)'
                            }}
                          >
                            {claimingStakes[stake.id] ? (
                              <div className="w-4 h-4 border-2 animate-spin" style={{
                                borderColor: 'rgba(0, 0, 0, 0.2)',
                                borderTopColor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: '50%'
                              }} />
                            ) : (
                              <>
                                <CheckCircle size={16} />
                                {t('staking.claim')}
                              </>
                            )}
                          </button>
                        ) : (
                          <div className="text-sm flex items-center gap-2 px-3 py-2" style={{
                            background: 'rgba(217, 18, 34, 0.1)',
                            borderRadius: '10px'
                          }}>
                            <Clock size={14} style={{ color: '#d91222' }} />
                            <span className="font-mono font-bold" style={{ color: '#d91222' }}>{timeRemaining}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12" style={{
                background: 'rgba(0, 0, 0, 0.02)',
                borderRadius: '16px'
              }}>
                <p className="mb-2 uppercase tracking-wider text-xs font-bold" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.noActiveStakes')}</p>
                <p className="text-xs" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.startStaking')}</p>
              </div>
            )}
          </div>
          
          {/* Completed Stakes */}
          {completedStakes.length > 0 && (
            <div className="relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              borderRadius: '24px',
              padding: '32px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #10b981, #059669)'
              }} />

              <div className="flex items-center gap-3 mb-6 pb-4" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <div className="w-10 h-10 flex items-center justify-center" style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))',
                  border: '2px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                }}>
                  <CheckCircle size={20} style={{ color: '#10b981' }} />
                </div>
                <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.completedStakes')}</h2>
              </div>

              <div className="space-y-4">
                {completedStakes.map((stake) => {
                  const asset = availableStakingAssets.find(a => a.symbol === stake.asset_symbol);

                  return (
                    <div key={stake.id} className="relative overflow-hidden group transition-all duration-300" style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(59, 130, 246, 0.05))',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      borderRadius: '20px',
                      padding: '20px',
                      boxShadow: '0 2px 12px rgba(16, 185, 129, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 12px rgba(16, 185, 129, 0.1)';
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.8), rgba(59, 130, 246, 0.8))'
                      }} />

                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {asset ? (
                            <div className="relative w-12 h-12 overflow-hidden flex items-center justify-center" style={{
                              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(59, 130, 246, 0.15))',
                              border: '2px solid rgba(16, 185, 129, 0.3)',
                              borderRadius: '14px',
                              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                            }}>
                              {renderCryptoIcon(asset)}
                            </div>
                          ) : (
                            <div className="w-12 h-12 flex items-center justify-center font-bold" style={{
                              background: 'rgba(16, 185, 129, 0.1)',
                              color: '#10b981',
                              border: '2px solid rgba(16, 185, 129, 0.2)',
                              borderRadius: '14px'
                            }}>
                              {stake.asset_symbol.substring(0, 2)}
                            </div>
                          )}
                          <div>
                            <div className="font-bold" style={{ color: '#1e293b' }}>{asset?.name || stake.asset_symbol}</div>
                            <div className="text-xs font-mono font-bold" style={{ color: 'rgba(0, 0, 0, 0.5)' }} translate="no">{stake.staked_amount.toFixed(stake.asset_symbol === 'USDT' ? 2 : 6)} {stake.asset_symbol}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono font-bold px-2 py-1" style={{
                            color: '#10b981',
                            background: 'rgba(16, 185, 129, 0.15)',
                            borderRadius: '6px'
                          }}>{stake.apy_rate}% APY</div>
                          <div className="text-xs font-bold mt-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                            {new Date(stake.start_date).toLocaleDateString()} - {new Date(stake.end_date).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.totalEarned')}</div>
                          <div className="font-mono font-bold px-2 py-1 inline-block" style={{
                            color: '#10b981',
                            background: 'rgba(16, 185, 129, 0.15)',
                            borderRadius: '6px'
                          }} translate="no">{stake.earned_amount.toFixed(6)} {stake.asset_symbol}</div>
                        </div>
                        <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider" style={{
                          background: 'rgba(16, 185, 129, 0.15)',
                          color: '#10b981',
                          border: '2px solid rgba(16, 185, 129, 0.3)',
                          borderRadius: '10px'
                        }}>
                          {t('staking.completed')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Staking Benefits */}
          <div className="relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #d91222, #b91020)'
            }} />

            <div className="flex items-center gap-3 mb-6 pb-4" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <div className="w-10 h-10 flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(185, 16, 32, 0.15))',
                border: '2px solid rgba(217, 18, 34, 0.3)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.2)'
              }}>
                <Shield size={20} style={{ color: '#d91222' }} />
              </div>
              <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.stakingBenefits')}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative overflow-hidden group transition-all duration-300" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.05), rgba(217, 18, 34, 0.03))',
                border: '2px solid rgba(217, 18, 34, 0.15)',
                borderRadius: '20px',
                padding: '24px',
                boxShadow: '0 2px 12px rgba(217, 18, 34, 0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(217, 18, 34, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(217, 18, 34, 0.08)';
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #d91222, #b91020)'
                }} />
                <div className="mb-4 w-12 h-12 flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{
                  background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(185, 16, 32, 0.15))',
                  border: '2px solid rgba(217, 18, 34, 0.3)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(217, 18, 34, 0.2)'
                }}>
                  <DollarSign size={24} style={{ color: '#d91222' }} />
                </div>
                <h3 className="text-sm font-bold mb-3 uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.passiveIncome')}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.whatIsStakingAnswer')}</p>
              </div>

              <div className="relative overflow-hidden group transition-all duration-300" style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(16, 185, 129, 0.03))',
                border: '2px solid rgba(16, 185, 129, 0.15)',
                borderRadius: '20px',
                padding: '24px',
                boxShadow: '0 2px 12px rgba(16, 185, 129, 0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(16, 185, 129, 0.08)';
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #10b981, #059669)'
                }} />
                <div className="mb-4 w-12 h-12 flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))',
                  border: '2px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                }}>
                  <Percent size={24} style={{ color: '#10b981' }} />
                </div>
                <h3 className="text-sm font-bold mb-3 uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.competitiveApy')}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Get some of the highest yields in the market with our optimized staking pools.</p>
              </div>

              <div className="relative overflow-hidden group transition-all duration-300" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.08), rgba(185, 16, 32, 0.08))',
                border: '2px solid rgba(217, 18, 34, 0.2)',
                borderRadius: '20px',
                padding: '24px',
                boxShadow: '0 2px 12px rgba(217, 18, 34, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(217, 18, 34, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(217, 18, 34, 0.15)';
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, rgba(217, 18, 34, 0.8), rgba(185, 16, 32, 0.8))'
                }} />
                <div className="mb-4 w-12 h-12 flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{
                  background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(185, 16, 32, 0.15))',
                  border: '2px solid rgba(217, 18, 34, 0.3)',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(217, 18, 34, 0.2)'
                }}>
                  <Lock size={24} style={{ color: '#d91222' }} />
                </div>
                <h3 className="text-sm font-bold mb-3 uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.flexibleTerms')}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Choose from various lock periods to match your investment strategy.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column - Active Stakes & Calculator */}
        <div className="space-y-8">
          {/* Staking Calculator */}
          <div className="relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #d91222, #b91020)'
            }} />

            <div className="flex items-center gap-3 mb-6 pb-4" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <div className="w-10 h-10 flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(185, 16, 32, 0.15))',
                border: '2px solid rgba(217, 18, 34, 0.3)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.2)'
              }}>
                <Calculator size={20} style={{ color: '#d91222' }} />
              </div>
              <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.stakingCalculator')}</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.selectAsset')}</label>
                <div className="relative">
                  <div
                    className="w-full px-4 py-3 pr-10 focus:outline-none transition-all font-mono font-bold cursor-pointer"
                    style={{
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.05) 100%)',
                      color: '#1e293b',
                      border: isAssetDropdownOpen ? '2px solid rgba(59, 130, 246, 0.5)' : '2px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '12px',
                      boxShadow: isAssetDropdownOpen ? '0 4px 12px rgba(59, 130, 246, 0.2)' : '0 2px 8px rgba(59, 130, 246, 0.1)'
                    }}
                    onClick={() => setIsAssetDropdownOpen(!isAssetDropdownOpen)}
                    onMouseEnter={(e) => {
                      if (!isAssetDropdownOpen) {
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isAssetDropdownOpen) {
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.1)';
                      }
                    }}
                  >
                    {availableStakingAssets.find(a => a.id === calculatorAsset)?.name} ({availableStakingAssets.find(a => a.id === calculatorAsset)?.apy}% APY)
                  </div>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center justify-center w-6 h-6 transition-transform" style={{
                    background: 'rgba(59, 130, 246, 0.15)',
                    borderRadius: '6px',
                    transform: isAssetDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)'
                  }}>
                    <ChevronDown size={16} style={{ color: '#3b82f6' }} />
                  </div>

                  {isAssetDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsAssetDropdownOpen(false)} />
                      <div className="absolute top-full left-0 right-0 mt-2 z-20 overflow-hidden" style={{
                        background: 'white',
                        border: '2px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.2)',
                        maxHeight: '250px',
                        overflowY: 'auto'
                      }}>
                        {availableStakingAssets.map((asset, index) => (
                          <div
                            key={asset.id}
                            className="px-4 py-3 cursor-pointer transition-all font-mono font-bold"
                            style={{
                              background: calculatorAsset === asset.id ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.08) 100%)' : 'transparent',
                              color: calculatorAsset === asset.id ? '#3b82f6' : '#1e293b',
                              borderBottom: index < availableStakingAssets.length - 1 ? '1px solid rgba(59, 130, 246, 0.1)' : 'none'
                            }}
                            onClick={() => {
                              setCalculatorAsset(asset.id);
                              setIsAssetDropdownOpen(false);
                            }}
                            onMouseEnter={(e) => {
                              if (calculatorAsset !== asset.id) {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(59, 130, 246, 0.03) 100%)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (calculatorAsset !== asset.id) {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <span>{asset.name}</span>
                              <span className="text-xs ml-2" style={{
                                background: 'rgba(59, 130, 246, 0.1)',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                color: '#3b82f6'
                              }}>{asset.apy}% APY</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('common.amount')}</label>
                <input
                  type="number"
                  placeholder={t('swap.enterAmount')}
                  className="w-full px-4 py-3 focus:outline-none transition-all font-mono font-bold"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.05) 100%)',
                    color: '#1e293b',
                    border: '2px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)'
                  }}
                  value={calculatorAmount}
                  onChange={(e) => setCalculatorAmount(e.target.value)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.1)';
                  }}
                />
              </div>

              <div>
                <label className="block text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.lockPeriod')} ({t('staking.days')})</label>
                <div className="relative">
                  <div
                    className="w-full px-4 py-3 pr-10 focus:outline-none transition-all font-mono font-bold cursor-pointer"
                    style={{
                      background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.08) 0%, rgba(217, 18, 34, 0.05) 100%)',
                      color: '#1e293b',
                      border: isDurationDropdownOpen ? '2px solid rgba(217, 18, 34, 0.5)' : '2px solid rgba(217, 18, 34, 0.3)',
                      borderRadius: '12px',
                      boxShadow: isDurationDropdownOpen ? '0 4px 12px rgba(217, 18, 34, 0.2)' : '0 2px 8px rgba(217, 18, 34, 0.1)'
                    }}
                    onClick={() => setIsDurationDropdownOpen(!isDurationDropdownOpen)}
                    onMouseEnter={(e) => {
                      if (!isDurationDropdownOpen) {
                        e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.5)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 18, 34, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDurationDropdownOpen) {
                        e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.3)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(217, 18, 34, 0.1)';
                      }
                    }}
                  >
                    {calculatorDuration} {t('staking.days')}
                  </div>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center justify-center w-6 h-6 transition-transform" style={{
                    background: 'rgba(217, 18, 34, 0.15)',
                    borderRadius: '6px',
                    transform: isDurationDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)'
                  }}>
                    <ChevronDown size={16} style={{ color: '#d91222' }} />
                  </div>

                  {isDurationDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsDurationDropdownOpen(false)} />
                      <div className="absolute top-full left-0 right-0 mt-2 z-20 overflow-hidden" style={{
                        background: 'white',
                        border: '2px solid rgba(217, 18, 34, 0.3)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 24px rgba(217, 18, 34, 0.2)'
                      }}>
                        {['30', '60', '90', '180', '365'].map((days, index) => (
                          <div
                            key={days}
                            className="px-4 py-3 cursor-pointer transition-all font-mono font-bold"
                            style={{
                              background: calculatorDuration === days ? 'linear-gradient(135deg, rgba(217, 18, 34, 0.12) 0%, rgba(217, 18, 34, 0.08) 100%)' : 'transparent',
                              color: calculatorDuration === days ? '#d91222' : '#1e293b',
                              borderBottom: index < 4 ? '1px solid rgba(217, 18, 34, 0.1)' : 'none'
                            }}
                            onClick={() => {
                              setCalculatorDuration(days);
                              setIsDurationDropdownOpen(false);
                            }}
                            onMouseEnter={(e) => {
                              if (calculatorDuration !== days) {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(217, 18, 34, 0.06) 0%, rgba(217, 18, 34, 0.03) 100%)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (calculatorDuration !== days) {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <span>{days} {t('staking.days')}</span>
                              {calculatorDuration === days && (
                                <CheckCircle size={16} style={{ color: '#d91222' }} />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-6 relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(59, 130, 246, 0.08))',
                border: '2px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '16px',
                padding: '20px',
                boxShadow: '0 4px 16px rgba(16, 185, 129, 0.15)'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.8), rgba(59, 130, 246, 0.8))'
                }} />

                <div className="text-center mb-4 pb-4" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                  <div className="text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('staking.estimatedEarnings')}</div>
                  <div className="text-2xl font-mono font-bold px-3 py-2 inline-block" style={{
                    color: '#10b981',
                    background: 'rgba(16, 185, 129, 0.15)',
                    borderRadius: '10px'
                  }} translate="no">
                    {calculatorResults.estimatedEarnings.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                  </div>
                  <div className="text-xs font-mono font-bold mt-2" style={{ color: 'rgba(0, 0, 0, 0.6)' }} translate="no">≈ ${calculatorResults.estimatedEarningsUsd.toFixed(2)}</div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3" style={{
                    background: 'rgba(59, 130, 246, 0.08)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '10px'
                  }}>
                    <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.daily')}</div>
                    <div className="font-mono text-sm font-bold" style={{ color: '#1e293b' }} translate="no">
                      {calculatorResults.daily.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                    </div>
                  </div>
                  <div className="p-3" style={{
                    background: 'rgba(16, 185, 129, 0.08)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    borderRadius: '10px'
                  }}>
                    <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.weekly')}</div>
                    <div className="font-mono text-sm font-bold" style={{ color: '#1e293b' }} translate="no">
                      {calculatorResults.weekly.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                    </div>
                  </div>
                  <div className="p-3" style={{
                    background: 'rgba(217, 18, 34, 0.08)',
                    border: '1px solid rgba(217, 18, 34, 0.2)',
                    borderRadius: '10px'
                  }}>
                    <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.monthly')}</div>
                    <div className="font-mono text-sm font-bold" style={{ color: '#1e293b' }} translate="no">
                      {calculatorResults.monthly.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                    </div>
                  </div>
                  <div className="p-3" style={{
                    background: 'rgba(59, 130, 246, 0.08)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '10px'
                  }}>
                    <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.yearly')}</div>
                    <div className="font-mono text-sm font-bold" style={{ color: '#1e293b' }} translate="no">
                      {calculatorResults.yearly.toFixed(6)} {availableStakingAssets.find(a => a.id === calculatorAsset)?.symbol}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Staking FAQ */}
          <div className="relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #d91222, #b91020)'
            }} />

            <div className="flex items-center gap-3 mb-6 pb-4" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <div className="w-10 h-10 flex items-center justify-center" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(185, 16, 32, 0.15))',
                border: '2px solid rgba(217, 18, 34, 0.3)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.2)'
              }}>
                <Info size={20} style={{ color: '#d91222' }} />
              </div>
              <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.stakingFaq')}</h2>
            </div>

            <div className="space-y-3">
              <div className="relative overflow-hidden group transition-all duration-300" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.03), rgba(217, 18, 34, 0.02))',
                border: '1px solid rgba(217, 18, 34, 0.15)',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 18, 34, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.15)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(217, 18, 34, 0.05)';
              }}>
                <h3 className="font-bold mb-2 text-sm uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.whatIsStaking')}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.whatIsStakingAnswer')}</p>
              </div>

              <div className="relative overflow-hidden group transition-all duration-300" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.03), rgba(217, 18, 34, 0.02))',
                border: '1px solid rgba(217, 18, 34, 0.15)',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 18, 34, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.15)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(217, 18, 34, 0.05)';
              }}>
                <h3 className="font-bold mb-2 text-sm uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.howAreRewardsCalculated')}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.howAreRewardsCalculatedAnswer')}</p>
              </div>

              <div className="relative overflow-hidden group transition-all duration-300" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.03), rgba(217, 18, 34, 0.02))',
                border: '1px solid rgba(217, 18, 34, 0.15)',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 18, 34, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.15)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(217, 18, 34, 0.05)';
              }}>
                <h3 className="font-bold mb-2 text-sm uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.canIUnstakeEarly')}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.canIUnstakeEarlyAnswer')}</p>
              </div>

              <div className="relative overflow-hidden group transition-all duration-300" style={{
                background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.03), rgba(217, 18, 34, 0.02))',
                border: '1px solid rgba(217, 18, 34, 0.15)',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 2px 8px rgba(217, 18, 34, 0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.3)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 18, 34, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.15)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(217, 18, 34, 0.05)';
              }}>
                <h3 className="font-bold mb-2 text-sm uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.whenDoIReceiveRewards')}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.whenDoIReceiveRewardsAnswer')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Staking Confirmation Modal */}
      {showConfirmationModal && selectedAsset && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0, 0, 0, 0.7)' }}>
          <div className="max-w-md w-full relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, rgba(217, 18, 34, 0.6), rgba(59, 130, 246, 0.6))'
            }} />

            <div className="flex items-center justify-between mb-6 pb-4" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.confirmStake')}</h3>
              <button
                onClick={() => setShowConfirmationModal(false)}
                className="transition-colors w-8 h-8 flex items-center justify-center"
                style={{
                  background: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '10px',
                  color: 'rgba(0, 0, 0, 0.5)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.color = 'rgba(0, 0, 0, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.color = 'rgba(0, 0, 0, 0.5)';
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-5">
              <div className="p-5" style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(16, 185, 129, 0.05))',
                border: '2px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '16px'
              }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative w-12 h-12 overflow-hidden flex items-center justify-center" style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(16, 185, 129, 0.15))',
                    border: '2px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '14px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
                  }}>
                    {renderCryptoIcon(selectedAsset)}
                  </div>
                  <div>
                    <div className="font-bold" style={{ color: '#1e293b' }}>{selectedAsset.name}</div>
                    <div className="text-xs font-mono font-bold px-2 py-0.5 inline-block" style={{
                      color: '#10b981',
                      background: 'rgba(16, 185, 129, 0.15)',
                      borderRadius: '6px'
                    }}>{selectedAsset.apy}% APY</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.amountToStake')}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={stakingAmount}
                        onChange={(e) => setStakingAmount(e.target.value)}
                        placeholder={`${t('common.min')} ${selectedAsset.minStake} ${selectedAsset.symbol}`}
                        className="w-full px-4 py-3 pr-24 focus:outline-none transition-all font-mono font-bold"
                        style={{
                          background: 'rgba(16, 185, 129, 0.08)',
                          color: '#1e293b',
                          border: '2px solid rgba(16, 185, 129, 0.2)',
                          borderRadius: '12px'
                        }}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                        <button
                          onClick={handleMaxClick}
                          className="text-xs transition-colors px-2 py-1 uppercase tracking-wider font-bold"
                          style={{
                            background: 'rgba(59, 130, 246, 0.15)',
                            color: '#3b82f6',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            borderRadius: '6px'
                          }}
                        >
                          {t('common.max')}
                        </button>
                        <span className="font-bold" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{selectedAsset.symbol}</span>
                      </div>
                    </div>
                    <div className="text-xs font-bold mt-2" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                      {t('common.available')}: {selectedAsset.symbol === 'USDT'
                        ? (availableBalance !== undefined ? availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : usdtBalance.toFixed(2))
                        : selectedAsset.balance?.toFixed(selectedAsset.symbol === 'USDT' ? 2 : 6)
                      } {selectedAsset.symbol}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>{t('staking.stakingPeriod')}</label>
                    <select
                      value={stakingDuration}
                      onChange={(e) => setStakingDuration(parseInt(e.target.value))}
                      className="w-full px-4 py-3 focus:outline-none transition-all font-mono font-bold"
                      style={{
                        background: 'rgba(217, 18, 34, 0.08)',
                        color: '#1e293b',
                        border: '2px solid rgba(217, 18, 34, 0.2)',
                        borderRadius: '12px'
                      }}
                    >
                      {selectedAsset.lockPeriods.map((period) => (
                        <option key={period} value={period}>{period} {t('staking.days')}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Estimated Returns */}
              <div className="p-5" style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(59, 130, 246, 0.08))',
                border: '2px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '16px'
              }}>
                <div className="flex items-center gap-2 mb-3 pb-3" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                  <span className="font-bold text-xs uppercase tracking-wider" style={{ color: '#1e293b' }}>{t('staking.estimatedReturns')}</span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-xs uppercase tracking-wider font-bold" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.apyRate')}</span>
                    <span className="font-mono font-bold px-2 py-0.5" style={{
                      color: '#10b981',
                      background: 'rgba(16, 185, 129, 0.15)',
                      borderRadius: '6px'
                    }}>{selectedAsset.apy}%</span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-xs uppercase tracking-wider font-bold" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.lockPeriod')}</span>
                    <span className="font-mono font-bold" style={{ color: '#1e293b' }} translate="no">{stakingDuration} {t('staking.days')}</span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-xs uppercase tracking-wider font-bold" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.estimatedEarnings')}</span>
                    <span className="font-mono font-bold px-2 py-0.5" style={{
                      color: '#10b981',
                      background: 'rgba(16, 185, 129, 0.15)',
                      borderRadius: '6px'
                    }} translate="no">
                      {calculateEstimatedReturn(
                        parseFloat(stakingAmount) || 0,
                        stakingDuration,
                        selectedAsset.apy
                      ).toFixed(6)} {selectedAsset.symbol}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-xs uppercase tracking-wider font-bold" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>{t('staking.unlockDate')}</span>
                    <span className="font-mono font-bold" style={{ color: '#1e293b' }}>
                      {new Date(Date.now() + stakingDuration * 24 * 60 * 60 * 1000).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Check for existing active stake */}
              {hasActiveStakeForAsset(selectedAsset.symbol) && (
                <div className="p-4 text-sm flex items-start gap-3" style={{
                  background: 'rgba(220, 38, 38, 0.1)',
                  border: '2px solid rgba(220, 38, 38, 0.3)',
                  borderRadius: '12px',
                  color: '#dc2626'
                }}>
                  <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold mb-1 uppercase tracking-wider text-xs">{t('staking.activeStakeExists')}</p>
                    <p className="text-xs font-semibold">{t('staking.alreadyHaveActiveStake', { asset: selectedAsset.symbol })}</p>
                  </div>
                </div>
              )}

              {/* Warning */}
              <div className="p-4 text-sm flex items-start gap-3" style={{
                background: 'rgba(220, 38, 38, 0.1)',
                border: '2px solid rgba(220, 38, 38, 0.3)',
                borderRadius: '12px',
                color: '#dc2626'
              }}>
                <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold mb-1 uppercase tracking-wider text-xs">{t('staking.importantInformation')}</p>
                  <p className="text-xs font-semibold">{t('staking.stakingWarning')}</p>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={confirmStake}
                disabled={isStaking || !stakingAmount || parseFloat(stakingAmount) <= 0 || hasActiveStakeForAsset(selectedAsset.symbol)}
                className="w-full py-3 font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-wider text-xs"
                style={{
                  background: (isStaking || !stakingAmount || parseFloat(stakingAmount) <= 0 || hasActiveStakeForAsset(selectedAsset.symbol))
                    ? 'rgba(0, 0, 0, 0.05)'
                    : 'linear-gradient(135deg, #d91222 0%, #b91020 100%)',
                  color: (isStaking || !stakingAmount || parseFloat(stakingAmount) <= 0 || hasActiveStakeForAsset(selectedAsset.symbol)) ? 'rgba(0, 0, 0, 0.3)' : '#FFFFFF',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: (isStaking || !stakingAmount || parseFloat(stakingAmount) <= 0 || hasActiveStakeForAsset(selectedAsset.symbol))
                    ? 'none'
                    : '0 4px 12px rgba(217, 18, 34, 0.3)'
                }}
              >
                {isStaking ? (
                  <div className="w-5 h-5 border-2 animate-spin" style={{
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    borderTopColor: '#FFFFFF',
                    borderRadius: '50%'
                  }} />
                ) : (
                  <>
                    <Layers size={18} />
                    {t('staking.confirmStake')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakingPage;